import {
  FieldAnswer,
  Patient,
  PatientScore,
  Score,
  ScoreInterval,
} from "@/src/data/allTypes";
import {
  fromNow,
  getItemId,
  getUserId,
  jsonAbstract,
  uid,
  updateOrPush,
} from "@/src/utils/utils";
import { useState } from "react";

import { Button } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import { CardType, FolderItem } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getUserShortName } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { replaceVariablesWithValues } from "../documents/variablesHook";
import ScoreCard from "./scoreCard";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import { AiAudioFormButton } from "../documents/aiAudioFormButton";

function ScoresCompartment({
  patient,
  parent,
  color,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  color?: CardType;
}) {
  const [reload, setReload] = useState(0);
  return (
    <CompartmentCard
      title={"Scores"}
      color={color || "red"}
      onClick={async () => {
        await openDrawer({
          id: "scoresListDrawer",
          title: "Ajouter un score",
          content: (
            <ScoresList
              patient={patient}
              onChange={(newValue) => {
                setReload((prev) => prev + 1);
                closeDrawer("scoresListDrawer");
              }}
            ></ScoresList>
          ),
        });
      }}
    >
      <PatientLastScoresList
        key={`patientMesuresList${reload}`}
        patient={patient}
        parent={parent}
        type={color}
      ></PatientLastScoresList>
    </CompartmentCard>
  );
}

function PatientLastScoresList({
  patient,
  parent,
  type,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  type?: CardType;
}) {
  let [searchKey, setSearchKey] = useState("");
  const [reload, setReload] = useState(0);
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["patientScoresList", searchKey, patient.id, parent?.id, reload],
    queryFn: () =>
      apiFetch(
        "patientScores",
        "loadLastPatientScores",
        { patientId: patient.id },
        "Erreur lors du chargement des scores"
      ),
  });

  async function openHistory({ patientScore }: { patientScore: PatientScore }) {
    await openDrawer({
      title: "Historique du score",
      content: (
        <ScoreHistory
          score={patientScore.score}
          patient={patient}
          onChange={() => {
            setReload((prev) => prev + 1);
          }}
        />
      ),
      footer: <div></div>,
    });
  }

  return (
    <ListConstructor
      data={data?.patientScores}
      isLoading={isLoading}
      isError={isError}
      error={error}
    >
      <div className="grid c1 gap10">
        {data?.patientScores?.map((patientScore) => {
          if (!patientScore) return null;
          let count = data?.counts?.[patientScore.score?.id || ""] || 0;
          return (
            <ScoreCard
              key={uid()}
              patientScore={patientScore}
              count={count}
              type={type}
              onClick={async () => {
                await openHistory({ patientScore });
              }}
              onSeeMoreClick={async () => {
                await openHistory({ patientScore });
              }}
            />
          );
        })}
      </div>
    </ListConstructor>
  );
}

function ScoreHistory({
  patient,
  score,
  onChange,
}: {
  patient: Patient;
  score: Score;
  onChange: () => void;
}) {
  const [reload, setReload] = useState(0);

  async function openPatientScoreDrawer(patientScore: PatientScore) {
    await openDrawer({
      title: "Ajouter un score",
      additionalClasses: ["medium"],

      content: (
        <PatientScoreForm
          patient={patient}
          current={patientScore}
          onChange={(newValue) => {
            patientScore = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        await apiFetch(
          "patientScores",
          "addOne",
          { patientScore },
          "Erreur lors de l'enregistrement "
        );
        setReload((prev) => prev + 1);
        onChange();
      },
    });
  }

  return (
    <FetchableList
      queryKey={["ScoreHistory", score.id, patient.id, reload]}
      queryFn={() =>
        apiFetch(
          "patientScores",
          "loadMany",
          { patientId: patient.id, scoreId: score.id },
          "Erreur lors du chargement de l'historique"
        )
      }
    >
      {(data) => (
        <div className="grid c1 gap10">
          {data?.map((patientScore) => {
            if (!patientScore) return null;
            let interval = patientScore.interval;
            return (
              <ScoreCard
                key={uid()}
                patientScore={patientScore}
                count={0}
                enableDelete={true}
                onClick={async () => {
                  await openPatientScoreDrawer(patientScore);
                }}
                onDelete={async () => {
                  setReload((prev) => prev + 1);
                  onChange();
                }}
                onSeeMoreClick={async () => {}}
              />
            );
          })}
        </div>
      )}
    </FetchableList>
  );
}

function PatientScoreForm({
  patient,
  current,
  onChange,
}: {
  patient: Patient;
  current: PatientScore;
  onChange: (newValue: PatientScore) => void;
}) {
  const [patientScore, setPatientScore] = useState(current);
  const score = patientScore.score;

  const { calculateScorePoints, getScoreInterval } = useScore();

  function setNewValue(newValue: PatientScore) {
    newValue.points = calculateScorePoints({ patientScore: newValue });
    newValue.interval = getScoreInterval({
      score,
      points: patientScore.points,
    });

    setPatientScore({ ...newValue });
    onChange({ ...newValue });
  }

  return (
    <div className="c1 gap20">
      <div
        style={{ position: "absolute", top: 10, right: 10, zIndex: 9999999 }}
      >
        <AiAudioFormButton
          patient={patient}
          reference={score}
          currentFieldAnswers={patientScore?.answers?.filter(
            (fieldAnswer) => fieldAnswer != null
          )}
          onSuccess={(fieldAnswers, edits) => {
            let newFieldAnswers = [...patientScore?.answers];
            for (let fieldAnswer of fieldAnswers) {
              if (!fieldAnswer) continue;
              let existingFieldAnswer = newFieldAnswers.find(
                (existingFieldAnswer) =>
                  getItemId(existingFieldAnswer?.field) ==
                  getItemId(fieldAnswer?.field)
              );

              if (existingFieldAnswer) {
                newFieldAnswers = newFieldAnswers.filter(
                  (existingFieldAnswer) =>
                    getItemId(existingFieldAnswer?.field) !=
                    getItemId(fieldAnswer?.field)
                );
              }
              newFieldAnswers.push(fieldAnswer);
            }

            setNewValue({ ...patientScore, answers: newFieldAnswers });
          }}
        ></AiAudioFormButton>
      </div>
      {score.sections?.map((section) => {
        return (
          <div key={section?.id} className="c1 gap25">
            {score.sections!.length > 1 ? <div>{section?.name}</div> : null}
            {section?.fields?.map((field) => {
              if (!field) return null;
              let fieldAnswer = patientScore.answers?.find(
                (fieldAnswer) =>
                  getItemId(fieldAnswer?.field) == getItemId(field)
              );

              console.log("fieldAnswer", {
                fieldAnswer,
                options: field?.options?.map((option) => {
                  return { value: option?.id, label: option?.name };
                }),
              });
              if (field?.type == "select") field.type = "chips";
              return (
                <Input
                  key={uid()}
                  name={section.fields!.length > 1 ? field?.name : null}
                  value={fieldAnswer?.value}
                  type={jsonAbstract(field.type)}
                  options={field?.options?.map((option) => {
                    return { value: option?.id, label: option?.name };
                  })}
                  placeholder={field.placeholder}
                  onChange={async (newValue) => {
                    patientScore.answers ||= [];
                    patientScore.answers = updateOrPush(
                      patientScore.answers,
                      {
                        field,
                        value: newValue,
                        parent: null,
                        patient,
                        creator: getUserId(),
                      },
                      (fieldAnswer) => fieldAnswer?.field?.id == field.id
                    );

                    setNewValue(patientScore);
                  }}
                ></Input>
              );
            })}
          </div>
        );
      })}

      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <div
        className="right mtop20 f15 p20 c1 bs br gap10"
        style={{
          position: "absolute",
          bottom: 60,
          right: 0,
          width: "100%",
          zIndex: 1000,

          background: "white",
        }}
      >
        <div className="fade bold" key={uid()}>
          <div className="tabContent">
            <div
              className="bold f15 "
              style={{ color: patientScore.interval?.color }}
            >
              {" "}
              {patientScore.points} Points
            </div>
            <div className="gray">{patientScore.interval?.interpretation}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function useScore() {
  function calculateScorePoints({
    patientScore,
  }: {
    patientScore: PatientScore;
  }): number {
    const score = patientScore.score;
    const { formulaType, formula } = score;

    if (formulaType == "sum") {
      let total = 0;
      patientScore.answers?.forEach((answer) => {
        let points = getAnswerPoints({ score, answer });
        total += points;
      });
      return total;
    }

    if (formulaType == "average") {
      let total = 0;
      patientScore.answers?.forEach((answer) => {
        let points = getAnswerPoints({ score, answer });
        total += points;
      });
      return total / (patientScore.answers?.length || 1);
    }

    if (formulaType == "custom") {
      console.log("it iiiis custom");
      let replacedFormula = replaceVariablesWithValues({
        content: formula,
        patientDocument: {
          content: "",
          creator: patientScore.creator,
          patient: patientScore.patient,
          status: "pending",
          refAnswer: {
            creator: patientScore.creator,
            patient: patientScore.patient,
            fieldAnswers: patientScore.answers,
          },
        },
      });
      try {
        return eval(replacedFormula);
      } catch (e) {
        console.log("error parsing score formula", replacedFormula);
        return 0;
      }
    }
    return 0;
  }

  function getAnswerPoints({
    score,
    answer,
  }: {
    score: Score;
    answer: FieldAnswer | null | undefined;
  }) {
    const intervals = score.fieldsIntervals?.filter(
      (interval) => interval?.field?.id == answer?.field?.id
    );

    const answerValue = answer?.value;

    for (let interval of intervals) {
      if (!interval) continue;
      const { type, min, max, value, points } = interval;
      if (type == "minMax" && min && max) {
        if (answerValue >= min && answerValue <= max)
          return parseFloat(points?.toString());
      }

      if (type == "points" && value == answerValue)
        return parseFloat(points?.toString());

      if (
        type == "points" &&
        value == "options:true" &&
        (answerValue === "true" || answerValue === true)
      )
        return parseFloat(points?.toString());

      if (
        type == "points" &&
        value == "options:false" &&
        (answerValue === "false" || answerValue === false)
      )
        return parseFloat(points?.toString());

      if (type == "value") return parseFloat(answerValue);
    }

    return 0;
  }

  function getScoreInterval({
    score,
    points,
  }: {
    score: Score;
    points: number;
  }): ScoreInterval | null {
    let intervals = score.intervals || [];
    let interval = intervals.find((interval) => {
      if (!interval) return false;
      return points >= interval.min && points <= interval.max;
    });
    return interval || null;
  }

  return { calculateScorePoints, getScoreInterval };
}

function ScoresList({
  patient,

  onChange,
}: {
  patient: Patient;
  onChange: (newValue: PatientScore) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState<string | null>("");

  async function openScoreDrawer(patientScore: PatientScore) {
    await openDrawer({
      id: "addScoreDrawer",
      title: "Ajouter un score",
      additionalClasses: ["medium"],
      content: (
        <PatientScoreForm
          patient={patient}
          current={patientScore}
          onChange={(newValue) => {
            patientScore = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        patientScore = await apiFetch(
          "patientScores",
          "addOne",
          { patientScore },
          "Erreur lors de l'enregistrement "
        );
        closeDrawer("addScoreDrawer");
        setReload((prev) => prev + 1);
        onChange(patientScore);
      },
    });
  }

  return (
    <FetchableList
      queryKey={[searchKey, reload]}
      queryFn={() =>
        apiFetch(
          "scores",
          "loadMany",
          { searchKey },
          "Erreur lors du chargement"
        )
      }
      searchKey={searchKey}
      onSearch={(searchKey) => {
        setSearchKey(searchKey);
      }}
    >
      {(data) => (
        <div className="grid c3 mc1 gap15">
          {data?.map((score) => {
            return (
              <Card
                key={uid()}
                onClick={async () => {
                  let patientScore: PatientScore = {
                    patient,
                    creator: getUserId(),
                    score,
                    answers: [],
                    points: 0,
                    interval: null,
                  };
                  await openScoreDrawer(patientScore);
                }}
              >
                <div>
                  <SearchKeyHighliter searchKey={searchKey}>
                    {score.name || ""}
                  </SearchKeyHighliter>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </FetchableList>
  );
}

export {
  PatientLastScoresList,
  PatientScoreForm,
  ScoresCompartment,
  ScoresList,
};
